import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import api from '../../api/axios';
import { StatusBadge, PriorityBadge } from '../../components/StatusBadge';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/kam/tickets/${id}`)
      .then(res => setTicket(res.data))
      .catch(() => navigate('/kam/tickets'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="text-center py-20 text-gray-400">Loading...</div>
    </div>
  );
  if (!ticket) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/kam/tickets')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1">
          ← Back to Tickets
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800">{ticket.ticketId}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{ticket.enterpriseName}</p>
            </div>
            <div className="flex gap-2">
              <PriorityBadge priority={ticket.priority} />
              <StatusBadge status={ticket.status} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div><span className="text-gray-400">Type:</span> <span className="font-medium ml-1">{ticket.ticketType || 'N/A'}</span></div>
            <div><span className="text-gray-400">Agent:</span> <span className="font-medium ml-1">{ticket.assignedAgentName || 'Unassigned'}</span></div>
            <div><span className="text-gray-400">Created:</span> <span className="font-medium ml-1">{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : 'N/A'}</span></div>
            {ticket.resolvedAt && (
              <div><span className="text-gray-400">Resolved:</span> <span className="font-medium ml-1">{new Date(ticket.resolvedAt).toLocaleString()}</span></div>
            )}
          </div>

          <div className="mb-4">
            <p className="text-sm font-medium text-gray-600 mb-1">Description</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{ticket.description}</p>
          </div>

          {ticket.aiReason && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
              <p className="text-xs font-semibold text-blue-700 mb-0.5">AI Classification Note</p>
              <p className="text-xs text-blue-600">{ticket.aiReason}</p>
            </div>
          )}

          {ticket.resolution && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-4">
              <p className="text-xs font-semibold text-green-700 mb-0.5">Resolution</p>
              <p className="text-xs text-green-600">{ticket.resolution}</p>
            </div>
          )}

          {ticket.attachmentUrl && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Attachment</p>
              {ticket.attachmentType === 'IMAGE' ? (
                <div className="border border-gray-200 rounded-xl overflow-hidden inline-block max-w-full">
                  <img
                    src={`http://localhost:8081${ticket.attachmentUrl}`}
                    alt="Ticket attachment"
                    className="max-w-full max-h-80 object-contain"
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
                  />
                  <a href={`http://localhost:8081${ticket.attachmentUrl}`} target="_blank" rel="noreferrer"
                    style={{ display: 'none' }}
                    className="text-sm text-primary underline">Open Image</a>
                </div>
              ) : (
                <a href={`http://localhost:8081${ticket.attachmentUrl}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark bg-blue-50 px-4 py-2 rounded-lg">
                  📄 View PDF Attachment
                </a>
              )}
            </div>
          )}
        </div>

        {ticket.history && ticket.history.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-700 mb-5">Timeline</h2>
            <div className="relative">
              <div className="absolute left-1.5 top-2 bottom-2 w-0.5 bg-gray-200"></div>
              <div className="space-y-5">
                {ticket.history.map((h, i) => (
                  <div key={i} className="flex gap-4 relative">
                    <div className="flex-shrink-0 w-3.5 h-3.5 bg-primary rounded-full mt-0.5 border-2 border-white shadow-sm z-10"></div>
                    <div className="flex-1 pb-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-gray-800">{h.action?.replace('_', ' ')}</p>
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{h.doneByRole}</span>
                      </div>
                      <p className="text-sm text-gray-600">{h.note}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {h.doneBy} &bull; {h.createdAt ? new Date(h.createdAt).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetail;
